import { Stack, ToggleButton, Tooltip, useTheme } from "@mui/material";
import { useTenantTranslation } from "hooks/formatters";
import { OVERLAY_STAGES, OverlayStage } from "hooks/usePostProcessingOverlay";

interface PostProcessingOverlayPanelProps {
  activeStage: OverlayStage;
  availableStages: Set<OverlayStage>;
  onSelect: (stage: OverlayStage) => void;
}

const STAGE_LABEL_KEYS: Record<OverlayStage, string> = {
  raw: "overlayStageRaw",
  smoothed: "overlayStageSmoothed",
  peaks: "overlayStagePeaks",
  argmax: "overlayStageArgmax",
};

export const PostProcessingOverlayPanel = ({
  activeStage,
  availableStages,
  onSelect,
}: PostProcessingOverlayPanelProps) => {
  const { t } = useTenantTranslation();
  const theme = useTheme();

  const buttonSx = {
    px: 1.5,
    py: 0.5,
    textTransform: "none",
    "&.Mui-selected": {
      backgroundColor: theme.palette.primary.main,
      color: theme.palette.primary.contrastText,
      "&:hover": { backgroundColor: theme.palette.primary.dark },
    },
  };

  return (
    <Stack
      direction="row"
      spacing={0.5}
      sx={{ justifyContent: "center" }}
      role="group"
      aria-label={t("postProcessingStage")}
    >
      {OVERLAY_STAGES.map((stage) => {
        const button = (
          <ToggleButton
            value={stage}
            size="small"
            color="primary"
            selected={stage === activeStage}
            disabled={!availableStages.has(stage)}
            onChange={() => {
              onSelect(stage);
            }}
            sx={buttonSx}
          >
            {t(STAGE_LABEL_KEYS[stage])}
          </ToggleButton>
        );

        if (availableStages.has(stage)) {
          return <span key={stage}>{button}</span>;
        }
        return (
          <Tooltip key={stage} title={t("overlayStageUnavailable")}>
            <span>{button}</span>
          </Tooltip>
        );
      })}
    </Stack>
  );
};
